"use client";

import { AccountForm } from "@/components/registrant/account-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import {
  cancelSecondaryUserInviteMutation,
  disableSecondaryUserMutation,
  getRegistrantAccountOptions,
  getRegistrantAccountUsersOptions,
  reenableSecondaryUserMutation,
  transferPrimaryOwnershipMutation,
  updateRegistrantOrgPaymentAccountMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { RegistrantAccountUserResponseSchema } from "@/hera-api/client/types.gen";
import { useRegistrantUserForm } from "@/hooks/use-registrant-user-form";
import { invalidateRegistrantUserQueries } from "@/lib/registrant-user-query-invalidation";
import { saveRegistrantUserAccessChanges } from "@/lib/save-registrant-user-access";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import type { AccountFormData } from "@/types/account";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ChangeBillingAccountDialog } from "./change-billing-account-dialog";
import { DisableUserDialog } from "./disable-user-dialog";
import { MakePrimaryDialog } from "./make-primary-dialog";
import { RegistrantUserPortalControls } from "./registrant-user-access-controls";
import {
  RegistrantUserActionButtons,
  getRegistrantUserActions,
} from "./registrant-user-actions";
import { UserRolesCell } from "./user-roles-cell";

interface RegistrantUserDetailsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
}

export function RegistrantUserDetailsSheet({
  isOpen,
  onOpenChange,
  userId,
}: RegistrantUserDetailsSheetProps) {
  const queryClient = useQueryClient();
  const { user, userData, isLoading, error, handleSubmit } =
    useRegistrantUserForm(userId);
  const { data: accountData } = useQuery({
    ...getRegistrantAccountOptions(),
    enabled: isOpen,
  });
  const { data: usersData } = useQuery({
    ...getRegistrantAccountUsersOptions(),
    enabled: isOpen,
  });
  const primaryAccountId = accountData?.primary_id ?? accountData?.id;
  const paymentAccountId = accountData?.payment_account_id ?? primaryAccountId;
  const currentBillingName =
    accountData?.payment_account_name ||
    accountData?.payment_account_email ||
    undefined;
  const [isMakePrimaryDialogOpen, setIsMakePrimaryDialogOpen] = useState(false);
  const [isBillingDialogOpen, setIsBillingDialogOpen] = useState(false);
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);

  const disableUserMutation = useMutation({
    ...disableSecondaryUserMutation(),
    onSuccess: () => {
      invalidateRegistrantUserQueries(queryClient);
      toast({
        title: "Success",
        description: "User has been disabled successfully",
      });
      setIsDisableDialogOpen(false);
      onOpenChange(false);
    },
    onError: (mutationError) => {
      toast({
        title: "Error",
        description: `Failed to disable user: ${getQueryErrorMessage(mutationError)}`,
        variant: "destructive",
      });
    },
  });
  const reenableUserMutation = useMutation({
    ...reenableSecondaryUserMutation(),
    onSuccess: () => {
      invalidateRegistrantUserQueries(queryClient);
      toast({
        title: "Success",
        description: "User has been re-enabled successfully",
      });
      onOpenChange(false);
    },
    onError: (mutationError) => {
      toast({
        title: "Error",
        description: `Failed to re-enable user: ${getQueryErrorMessage(mutationError)}`,
        variant: "destructive",
      });
    },
  });
  const cancelInviteMutation = useMutation({
    ...cancelSecondaryUserInviteMutation(),
    onSuccess: () => {
      invalidateRegistrantUserQueries(queryClient);
      toast({
        title: "Success",
        description: "Invite has been cancelled",
      });
      onOpenChange(false);
    },
    onError: (mutationError) => {
      toast({
        title: "Error",
        description: `Failed to cancel invite: ${getQueryErrorMessage(mutationError)}`,
        variant: "destructive",
      });
    },
  });
  const updateBillingAccountMutation = useMutation({
    ...updateRegistrantOrgPaymentAccountMutation(),
    onSuccess: () => {
      setIsBillingDialogOpen(false);
      invalidateRegistrantUserQueries(queryClient);
      toast({
        title: "Success",
        description: "Billing account has been updated",
      });
    },
    onError: (mutationError) => {
      toast({
        title: "Error",
        description: `Failed to change the billing account: ${getQueryErrorMessage(mutationError)}`,
        variant: "destructive",
      });
    },
  });
  const transferPrimaryMutation = useMutation({
    ...transferPrimaryOwnershipMutation(),
    onSuccess: () => {
      invalidateRegistrantUserQueries(queryClient);
      toast({
        title: "Success",
        description: "Primary ownership has been transferred successfully",
      });
      setIsMakePrimaryDialogOpen(false);
      onOpenChange(false);
    },
    onError: (mutationError) => {
      toast({
        title: "Error",
        description: `Failed to transfer primary ownership: ${getQueryErrorMessage(mutationError)}`,
        variant: "destructive",
      });
    },
  });

  const isDisabledUser = user?.status === "disabled";
  const isPendingUser = user?.status === "pending";
  const userName = user?.name || user?.email || "this user";
  const [draftUser, setDraftUser] =
    useState<RegistrantAccountUserResponseSchema | null>(null);

  useEffect(() => {
    setDraftUser(user);
  }, [user]);

  const displayUser = draftUser ?? user;
  const sheetActions = user
    ? getRegistrantUserActions({
        user,
        paymentAccountId,
      })
    : [];

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>User Details</SheetTitle>
          <SheetDescription>
            {isDisabledUser
              ? "This user is disabled. Re-enable them to restore login access."
              : isPendingUser
                ? "This invite has not been accepted yet. Roles and portal access apply when they log in."
                : "View and update this user’s profile, roles, and portal access."}
          </SheetDescription>
        </SheetHeader>
        <div className="overflow-y-auto max-h-[calc(100vh-120px)] px-4 pb-6 space-y-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <LoadingScreen />
            </div>
          ) : error ? (
            <ErrorComponent
              title={getQueryErrorHeading(error)}
              message={getQueryErrorMessage(error)}
            />
          ) : (
            <>
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle>Details</CardTitle>
                    {displayUser ? (
                      <UserRolesCell
                        row={displayUser}
                        paymentAccountId={paymentAccountId}
                        editable={!isDisabledUser}
                        onRowChange={
                          isDisabledUser || isPendingUser
                            ? undefined
                            : setDraftUser
                        }
                      />
                    ) : null}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {isPendingUser ? (
                    <p className="text-sm text-muted-foreground">
                      {user?.email}
                    </p>
                  ) : (
                    <AccountForm
                      initialData={userData || undefined}
                      hideSubmit={isDisabledUser}
                      embedded
                      onSubmit={async (data: AccountFormData) => {
                        try {
                          if (user && displayUser) {
                            await saveRegistrantUserAccessChanges({
                              original: user,
                              draft: displayUser,
                            });
                          }
                        } catch (saveError) {
                          toast({
                            title: "Error",
                            description: getQueryErrorMessage(saveError),
                            variant: "destructive",
                          });
                          return;
                        }
                        try {
                          await handleSubmit({
                            email: data.email,
                            name: data.name,
                            phone: data.phone,
                            company_name: data.company_name,
                          });
                          invalidateRegistrantUserQueries(queryClient);
                          onOpenChange(false);
                        } catch {
                          // Profile save already showed a toast
                        }
                      }}
                      submitButtonText="Save Changes"
                    />
                  )}
                </CardContent>
              </Card>
              {displayUser ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle>Portal access</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <RegistrantUserPortalControls
                      user={displayUser}
                      primaryAccountId={primaryAccountId}
                      onUserChange={
                        isDisabledUser || isPendingUser
                          ? undefined
                          : setDraftUser
                      }
                      disabled={isDisabledUser}
                    />
                  </CardContent>
                </Card>
              ) : null}
              {user && sheetActions.length > 0 ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle>Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <RegistrantUserActionButtons
                      user={user}
                      paymentAccountId={paymentAccountId}
                      onBilling={() => setIsBillingDialogOpen(true)}
                      onMakePrimary={() => setIsMakePrimaryDialogOpen(true)}
                      onDisable={() => {
                        setIsDisableDialogOpen(true);
                      }}
                      onReenable={() => {
                        if (!userId) return;
                        reenableUserMutation.mutate({
                          path: { user_id: userId },
                        });
                      }}
                      onCancelInvite={() => {
                        if (!userId) return;
                        cancelInviteMutation.mutate({
                          path: { user_id: userId },
                        });
                      }}
                      busy={{
                        billing: updateBillingAccountMutation.isPending,
                        makePrimary: transferPrimaryMutation.isPending,
                        disable: disableUserMutation.isPending,
                        reenable: reenableUserMutation.isPending,
                        cancelInvite: cancelInviteMutation.isPending,
                      }}
                    />
                  </CardContent>
                </Card>
              ) : null}
            </>
          )}
        </div>
      </SheetContent>
      {userId && (
        <ChangeBillingAccountDialog
          isOpen={isBillingDialogOpen}
          onOpenChange={setIsBillingDialogOpen}
          onConfirm={() => {
            if (!userId) return;
            updateBillingAccountMutation.mutate({
              body: { payment_account_id: userId },
            });
          }}
          newBillingName={userName}
          currentBillingName={currentBillingName}
          isLoading={updateBillingAccountMutation.isPending}
        />
      )}
      <DisableUserDialog
        user={isDisableDialogOpen ? user : null}
        users={usersData?.users ?? []}
        isLoading={disableUserMutation.isPending}
        onOpenChange={setIsDisableDialogOpen}
        onConfirm={() => {
          if (!userId) return;
          disableUserMutation.mutate({
            path: { user_id: userId },
          });
        }}
      />
      {userId && (
        <MakePrimaryDialog
          isOpen={isMakePrimaryDialogOpen}
          onOpenChange={setIsMakePrimaryDialogOpen}
          onConfirm={(moveBillingToNewPrimary) => {
            transferPrimaryMutation.mutate({
              path: { user_id: userId },
              body: {
                move_billing_to_new_primary: moveBillingToNewPrimary,
              },
            });
          }}
          userName={userName}
          currentBillingName={currentBillingName}
          isNewPrimaryAlreadyBilling={paymentAccountId === userId}
          isLoading={transferPrimaryMutation.isPending}
        />
      )}
    </Sheet>
  );
}
